/**
 * Share, members and the Viewer role (MOSA-367 slice 1).
 *
 * The API is the rule: `canWriteRows` in onlydash-api decides every write, and
 * a Viewer gets 403 with `code: "view_only"` whatever this file says. What
 * lives here is only what the panel needs in order to not offer a control that
 * the server will refuse, plus the copy that goes with it.
 */

/** The roles the Share dialog offers. */
export const INVITABLE_ROLES = ["EDITOR", "VIEWER", "CUSTOM"] as const;

export type InvitableRole = (typeof INVITABLE_ROLES)[number];

/** Board decision 2026-10-25: Viewer is the default. */
export const DEFAULT_INVITE_ROLE: InvitableRole = "VIEWER";

export const TAB_LEVELS = ["NONE", "VIEW", "EDIT"] as const;

export type TabLevel = (typeof TAB_LEVELS)[number];

/** One condition on a View or Edit tab. The API stores at most one. */
export const ROW_FILTER_OPS = ["IS", "IS_ONE_OF", "CONTAINS"] as const;

export type RowFilterOp = (typeof ROW_FILTER_OPS)[number];

/**
 * The value token the API replaces with the invited address.
 * The panel says "the member's email" wherever this token is stored.
 */
export const MEMBER_EMAIL_TOKEN = "{member.email}";

export type RowFilter = {
  column: string;
  op: RowFilterOp;
  value: string | string[];
};

export type TabRule = { tab: string; level: TabLevel; filter?: RowFilter };

export type AccessKind = "OWNER" | "EDITOR" | "VIEWER" | "CUSTOM";

/** The role the API ships on the datasource for the caller. */
export type ViewerRole =
  | "ADMIN"
  | "MEMBER"
  | "VIEWER"
  | "CUSTOM"
  | undefined
  | null;

/** The 403 body code the API sends when a Viewer calls a write route. */
export const VIEW_ONLY_CODE = "view_only";

/** What a 404 on a hidden table or row says. The API uses the same sentence. */
export const HIDDEN_RECORD_MESSAGE = "Row not found or not shared with you";

/**
 * A member whose row filter names a missing or ambiguous column.
 * The list is empty. This is the sentence on the grid and on search.
 */
export const ACCESS_UPDATING_MESSAGE = "Your access is being updated";

/** A 403 when a save would move a row outside the member's filter. */
export const OUT_OF_SCOPE_CODE = "out_of_scope";

/** The sentence next to the form. The API uses the same words. */
export const OUT_OF_SCOPE_MESSAGE = "This row is outside your access.";

/** A Viewer sees a read-only grid and the view-only banner. */
export const isViewOnly = (role: ViewerRole): boolean => role === "VIEWER";

/** Only the owner sees Share and the member list (Q17 start value). */
export const canManageMembers = (role: ViewerRole): boolean => role === "ADMIN";

export const normaliseLevel = (level: string | null | undefined): TabLevel | null => {
  const upper = (level ?? "").trim().toUpperCase();
  if (upper === "NONE" || upper === "VIEW" || upper === "EDIT") return upper;
  return null;
};

/** `access` is the slice 2a name. `accessRole` is the slice 1 name. */
export const normaliseAccess = (
  access: string | null | undefined,
): AccessKind | null => {
  const upper = (access ?? "").trim().toUpperCase();
  if (upper === "OWNER" || upper === "ADMIN") return "OWNER";
  if (upper === "EDITOR" || upper === "MEMBER") return "EDITOR";
  if (upper === "VIEWER") return "VIEWER";
  if (upper === "CUSTOM") return "CUSTOM";
  return null;
};

const accessFromViewerRole = (role: ViewerRole): AccessKind | null =>
  normaliseAccess(role ?? undefined);

export const tabLevelLabel = (level: TabLevel): string => {
  if (level === "EDIT") return "Edit";
  if (level === "VIEW") return "View";
  return "No access";
};

const isFilterOp = (op: string): op is RowFilterOp =>
  (ROW_FILTER_OPS as readonly string[]).includes(op);

/**
 * A column the owner has picked, even when the value is still empty.
 * The editor keeps that draft. The request does not send it.
 */
export const draftFilter = (
  level: TabLevel,
  filter: RowFilter | null | undefined,
): RowFilter | undefined => {
  if (level === "NONE" || !filter) return undefined;
  const column = typeof filter.column === "string" ? filter.column.trim() : "";
  if (!column) return undefined;
  const op = typeof filter.op === "string" && isFilterOp(filter.op) ? filter.op : "IS";
  return { column, op, value: filter.value ?? "" };
};

/** A filter the API will accept. An empty value is not one. */
export const wireFilter = (
  filter: RowFilter | null | undefined,
): RowFilter | undefined => {
  const draft = filter ? draftFilter("VIEW", filter) : undefined;
  if (!draft) return undefined;
  if (draft.op === "IS_ONE_OF") {
    const values = (Array.isArray(draft.value) ? draft.value : [draft.value])
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (!values.length) return undefined;
    return { column: draft.column, op: "IS_ONE_OF", value: values };
  }
  const text = (
    Array.isArray(draft.value) ? draft.value.join(", ") : draft.value
  ).trim();
  if (!text) return undefined;
  return { column: draft.column, op: draft.op, value: text };
};

/** Tables where the owner picked a column and has not entered a value yet. */
export const incompleteRowFilters = (rules: TabRule[]): string[] =>
  rules
    .filter(
      (rule) => rule.level !== "NONE" && !!rule.filter && !wireFilter(rule.filter),
    )
    .map((rule) => rule.tab);

/** The line under the editor when a row rule still needs a value. */
export const rowFilterGapMessage = (tabs: string[]): string | null => {
  if (tabs.length === 0) return null;
  if (tabs.length === 1) return `Enter a value for ${tabs[0]}.`;
  return `Enter a value for ${tabs.join(", ")}.`;
};

/**
 * One rule per table in the latest analysis.
 * A table with no rule is No access. A rule for any other table is dropped.
 * A row filter stays on a View or Edit rule. No access drops it.
 */
export const alignTabRules = (
  tabs: string[],
  rules: TabRule[] | null | undefined,
): TabRule[] => {
  const byTab = new Map<string, TabRule>();
  for (const rule of rules ?? []) {
    const level = normaliseLevel(rule?.level);
    if (!rule?.tab || !level) continue;
    const filter = draftFilter(level, rule.filter);
    byTab.set(
      rule.tab,
      filter ? { tab: rule.tab, level, filter } : { tab: rule.tab, level },
    );
  }
  return tabs.map(
    (tab) => byTab.get(tab) ?? { tab, level: "NONE" as TabLevel },
  );
};

const namesAt = (rules: TabRule[], level: TabLevel): string[] =>
  rules.filter((rule) => rule.level === level).map((rule) => rule.tab);

const plainNames = (rules: TabRule[], level: TabLevel): string[] =>
  rules
    .filter((rule) => rule.level === level && !rule.filter)
    .map((rule) => rule.tab);

const filterValueText = (value: string | string[]): string => {
  const parts = Array.isArray(value) ? value : [value];
  return parts
    .map((part) =>
      part === MEMBER_EMAIL_TOKEN ? "the member's email" : part,
    )
    .join(", ");
};

/** The clause after a table name. "where region is North". */
export const filterClause = (filter: RowFilter): string => {
  const value = filterValueText(filter.value);
  if (filter.op === "IS") return `where ${filter.column} is ${value}`;
  if (filter.op === "CONTAINS") {
    return `where ${filter.column} contains ${value}`;
  }
  return `where ${filter.column} is one of ${value}`;
};

/**
 * The preview under the role control when the owner picks Custom.
 * A table with a row filter gets its own sentence, so the filter is visible.
 */
export const customPreviewLine = (rules: TabRule[]): string => {
  const edit = plainNames(rules, "EDIT");
  const view = plainNames(rules, "VIEW");
  const hidden = namesAt(rules, "NONE");
  const parts: string[] = [];
  if (edit.length) parts.push(`Can edit ${edit.join(", ")}.`);
  for (const rule of rules) {
    if (rule.level === "EDIT" && rule.filter) {
      parts.push(`Can edit ${rule.tab} ${filterClause(rule.filter)}.`);
    }
  }
  if (view.length) parts.push(`Can view ${view.join(", ")}.`);
  for (const rule of rules) {
    if (rule.level === "VIEW" && rule.filter) {
      parts.push(`Can view ${rule.tab} ${filterClause(rule.filter)}.`);
    }
  }
  if (hidden.length) parts.push(`Can't see ${hidden.join(", ")}.`);
  if (!parts.length) return "This panel has no tables yet.";
  return parts.join(" ");
};

/** The member-list line. Shorter than the preview. A filter stays in it. */
export const customAccessSummary = (rules: TabRule[]): string => {
  const edit = plainNames(rules, "EDIT");
  const view = plainNames(rules, "VIEW");
  const hidden = namesAt(rules, "NONE");
  const parts: string[] = [];
  if (edit.length) parts.push(`Edit ${edit.join(", ")}`);
  for (const rule of rules) {
    if (rule.level === "EDIT" && rule.filter) {
      parts.push(`Edit ${rule.tab} ${filterClause(rule.filter)}`);
    }
  }
  if (view.length) parts.push(`View ${view.join(", ")}`);
  for (const rule of rules) {
    if (rule.level === "VIEW" && rule.filter) {
      parts.push(`View ${rule.tab} ${filterClause(rule.filter)}`);
    }
  }
  if (hidden.length) parts.push(`No ${hidden.join(", ")}`);
  if (!parts.length) return "No table access.";
  return `${parts.join(". ")}.`;
};

/** True when the stored value is only the member's email. */
export const usesMemberEmail = (
  value: string | string[] | null | undefined,
): boolean => {
  if (value === MEMBER_EMAIL_TOKEN) return true;
  return (
    Array.isArray(value) &&
    value.length === 1 &&
    value[0] === MEMBER_EMAIL_TOKEN
  );
};

export const filterAsMemberEmail = (filter: RowFilter): RowFilter =>
  filter.op === "IS_ONE_OF"
    ? { ...filter, value: [MEMBER_EMAIL_TOKEN] }
    : { ...filter, value: MEMBER_EMAIL_TOKEN };

export const filterWithoutMemberEmail = (filter: RowFilter): RowFilter =>
  filter.op === "IS_ONE_OF" ? { ...filter, value: [] } : { ...filter, value: "" };

/** Keep the value when the owner changes is / is one of / contains. */
export const filterWithOp = (filter: RowFilter, op: RowFilterOp): RowFilter => {
  if (usesMemberEmail(filter.value)) return filterAsMemberEmail({ ...filter, op });
  if (op === "IS_ONE_OF") {
    if (Array.isArray(filter.value)) return { ...filter, op };
    const values = filter.value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    return { ...filter, op, value: values };
  }
  const text = Array.isArray(filter.value)
    ? filter.value.join(", ")
    : filter.value;
  return { ...filter, op, value: text };
};

/** The text in the value box. The member-email token is not typed. */
export const filterValueDraft = (filter: RowFilter): string => {
  if (usesMemberEmail(filter.value)) return "";
  if (Array.isArray(filter.value)) return filter.value.join(", ");
  return filter.value;
};

export const filterFromDraft = (filter: RowFilter, text: string): RowFilter => {
  if (filter.op === "IS_ONE_OF") {
    return {
      ...filter,
      value: text
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean),
    };
  }
  return { ...filter, value: text };
};

export const totalBrokenRules = (
  members: Array<{ brokenRuleCount?: number | null }>,
): number =>
  members.reduce((sum, member) => {
    const count = member.brokenRuleCount;
    return sum + (typeof count === "number" && count > 0 ? Math.floor(count) : 0);
  }, 0);

export const firstBrokenMember = <
  T extends { id: string; brokenRuleCount?: number | null },
>(
  members: T[],
): T | null => members.find((member) => (member.brokenRuleCount ?? 0) > 0) ?? null;

/** The owner banner. Null when nothing is broken. */
export const brokenRuleBannerLabel = (count: number): string | null => {
  if (!Number.isFinite(count) || count <= 0) return null;
  const n = Math.floor(count);
  if (n <= 0) return null;
  return n === 1 ? "Fix 1 broken access rule" : `Fix ${n} broken access rules`;
};

/** The list body sets this when the row filter cannot be applied. */
export const listRuleBroken = (
  payload: { rule_broken?: unknown; items?: unknown } | null | undefined,
): boolean => payload?.rule_broken === true;

/** The one preview line under the role control. */
export const rolePreviewLine = (
  role: InvitableRole,
  rules: TabRule[] = [],
): string => {
  if (role === "EDITOR") return "Can view and edit this panel.";
  if (role === "CUSTOM") return customPreviewLine(rules);
  return "Can view this panel.";
};

export const roleLabel = (role: InvitableRole): string => {
  if (role === "EDITOR") return "Editor";
  if (role === "CUSTOM") return "Custom";
  return "Viewer";
};

/** The radio the owner clicks. Custom opens the per-table rows. */
export const roleChoiceLabel = (role: InvitableRole): string =>
  role === "CUSTOM" ? "Custom…" : roleLabel(role);

/** Custom is the only role that carries table rules. An empty filter is omitted. */
export const accessRequestBody = (
  role: InvitableRole,
  tabRules: TabRule[],
): { role: InvitableRole; tabRules?: TabRule[] } => {
  if (role !== "CUSTOM") return { role };
  return {
    role,
    tabRules: tabRules.map((rule) => {
      if (rule.level === "NONE") return { tab: rule.tab, level: rule.level };
      const filter = wireFilter(rule.filter);
      return filter
        ? { tab: rule.tab, level: rule.level, filter }
        : { tab: rule.tab, level: rule.level };
    }),
  };
};

/** Share, with the member the banner should open already selected. */
export const memberOpenPath = (
  datasourceId: string,
  memberId: string,
): string =>
  `${membersPath(datasourceId)}?member=${encodeURIComponent(memberId)}`;

export type CallerTabMode = "write" | "view" | "hidden";

const levelOn = (
  rules: TabRule[] | null | undefined,
  tab: string | null | undefined,
): TabLevel | null => {
  if (!tab) return null;
  const rule = (rules ?? []).find((entry) => entry.tab === tab);
  return rule ? normaliseLevel(rule.level) : null;
};

/**
 * What this caller can do on one table.
 * A Viewer is view-only on every table. Custom reads the rule for that table.
 * No rule on a Custom member is hidden (fail closed).
 */
export const callerTabAccess = (input: {
  viewerRole?: ViewerRole;
  access?: string | null;
  tab?: string | null;
  tabRules?: TabRule[] | null;
  collectionLevel?: string | null;
}): CallerTabMode => {
  const kind =
    normaliseAccess(input.access) ?? accessFromViewerRole(input.viewerRole);
  if (kind === "VIEWER") return "view";
  if (kind !== "CUSTOM") return "write";
  const fromCollection = normaliseLevel(input.collectionLevel);
  if (fromCollection === "EDIT") return "write";
  if (fromCollection === "VIEW") return "view";
  if (fromCollection === "NONE") return "hidden";
  // A non-empty rule list is the contract. A table with no rule is hidden.
  // An empty list means the schema is already this member's tables, and a
  // write stays closed until a level arrives.
  if (input.tabRules && input.tabRules.length > 0) {
    const level = levelOn(input.tabRules, input.tab);
    if (level === "EDIT") return "write";
    if (level === "VIEW") return "view";
    return "hidden";
  }
  return "view";
};

type NamedCollection = {
  name?: string | null;
  level?: string | null;
  accessLevel?: string | null;
};

/**
 * Tables this caller may see.
 * Owner, Editor and Viewer keep the list the API sent.
 * Custom drops No access when the payload includes rules or a level.
 * With neither, the API list is already the member's tables.
 */
export const visibleCollections = <T extends NamedCollection>(
  collections: T[] | null | undefined,
  access: string | null | undefined,
  tabRules?: TabRule[] | null,
): T[] => {
  const list = collections ?? [];
  if (normaliseAccess(access) !== "CUSTOM") return list;
  const rules = tabRules ?? [];
  const hasFieldLevel = list.some((collection) =>
    normaliseLevel(collection.level ?? collection.accessLevel),
  );
  if (!hasFieldLevel && rules.length === 0) return list;
  return list.filter((collection) => {
    const level =
      normaliseLevel(collection.level ?? collection.accessLevel) ??
      levelOn(rules, collection.name);
    return level === "VIEW" || level === "EDIT";
  });
};

type RelationLike = {
  targetCollectionId?: string | null;
  targetCollection?: { name?: string | null } | null;
};

type ScopedCollection = NamedCollection & { id?: string | null };

type DashboardLike = {
  name?: string | null;
  dataTables?: Array<{ collection?: string | null }> | null;
  charts?: Array<{ collections?: Array<string | null> | null }> | null;
  forms?: Array<{
    fields?: Array<{ collection?: string | null }> | null;
  }> | null;
};

type AttemptLike = {
  status?: string | null;
  databaseCollections?: Array<
    ScopedCollection & {
      relations?: RelationLike[] | null;
      targetRelations?: RelationLike[] | null;
    }
  > | null;
  databaseRelations?: RelationLike[] | null;
  progress?: { current?: string | null } | null;
  uiGenerationAttempts?: Array<{
    panels?: Array<{ dashboards?: DashboardLike[] | null }> | null;
  }> | null;
};

const dashboardTableNames = (dashboard: DashboardLike): string[] => {
  const names: string[] = [];
  for (const table of dashboard.dataTables ?? []) {
    if (table?.collection) names.push(table.collection);
  }
  for (const chart of dashboard.charts ?? []) {
    for (const name of chart?.collections ?? []) {
      if (name) names.push(name);
    }
  }
  for (const form of dashboard.forms ?? []) {
    for (const field of form?.fields ?? []) {
      if (field?.collection) names.push(field.collection);
    }
  }
  return names;
};

/**
 * The datasource list still includes every table. The latest analysis does not:
 * a hidden table is already gone from that response. Keep the rich list, and
 * drop any table the scoped analysis did not return. `null` means the scoped
 * read failed, so the list stays as it arrived.
 */
export const datasourceForMember = <
  T extends {
    access?: string | null;
    viewerRole?: string | null;
    analyzeAttempts?: AttemptLike[] | null;
  },
>(
  datasource: T,
  scoped: readonly ScopedCollection[] | null,
): T => {
  if (scoped == null) return datasource;
  const access = datasource.access ?? datasource.viewerRole;
  if (normaliseAccess(access) !== "CUSTOM") return datasource;
  const attempts = datasource.analyzeAttempts;
  if (!attempts?.length) return datasource;

  const allow = new Set(
    scoped.map((row) => row.name).filter((name): name is string => !!name),
  );
  const levelByName = new Map<string, string>();
  for (const row of scoped) {
    const level = row.level ?? row.accessLevel;
    if (row.name && level) levelByName.set(row.name, level);
  }

  const nextAttempts = attempts.map((attempt) => {
    if (attempt.status && attempt.status !== "SUCCESS") return attempt;
    const collections = attempt.databaseCollections ?? [];
    const hiddenIds = new Set(
      collections
        .filter((row) => row.id && row.name && !allow.has(row.name))
        .map((row) => row.id as string),
    );
    const keepRelation = (relation: RelationLike) => {
      if (
        relation?.targetCollectionId &&
        hiddenIds.has(relation.targetCollectionId)
      ) {
        return false;
      }
      const target = relation?.targetCollection?.name;
      return !target || allow.has(target);
    };
    const visible = collections
      .filter((row) => !!row.name && allow.has(row.name))
      .map((row) => {
        const level = row.name ? levelByName.get(row.name) : undefined;
        const next = { ...row };
        if (
          level &&
          !normaliseLevel(next.level ?? next.accessLevel)
        ) {
          next.level = level;
        }
        if (Array.isArray(row.relations)) {
          next.relations = row.relations.filter(keepRelation);
        }
        if (Array.isArray(row.targetRelations)) {
          next.targetRelations = row.targetRelations.filter(keepRelation);
        }
        return next;
      });
    const progress = attempt.progress;
    const nextProgress =
      progress &&
      typeof progress.current === "string" &&
      !allow.has(progress.current)
        ? { ...progress, current: null }
        : progress;
    const generations = attempt.uiGenerationAttempts?.map((generation) => ({
      ...generation,
      panels: generation.panels?.map((panel) => ({
        ...panel,
        dashboards: (panel.dashboards ?? []).filter((dashboard) =>
          dashboardTableNames(dashboard).every((name) => allow.has(name)),
        ),
      })),
    }));
    return {
      ...attempt,
      databaseCollections: visible,
      databaseRelations: Array.isArray(attempt.databaseRelations)
        ? attempt.databaseRelations.filter(keepRelation)
        : attempt.databaseRelations,
      progress: nextProgress,
      uiGenerationAttempts: generations ?? attempt.uiGenerationAttempts,
    };
  });

  return { ...datasource, analyzeAttempts: nextAttempts };
};

export const httpStatusOf = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } })?.response
    ?.status;
  return typeof status === "number" ? status : undefined;
};

/** A 404 on a table or a row uses the contract sentence. Other errors keep the API text. */
export const accessErrorMessage = (error: unknown, fallback: string): string => {
  if (httpStatusOf(error) === 404) return HIDDEN_RECORD_MESSAGE;
  const message = (
    error as { response?: { data?: { message?: unknown } } }
  )?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

/** Where the Share button goes. */
export const membersPath = (datasourceId: string): string =>
  `/datasources/${datasourceId}/members`;

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normaliseEmail = (email: string): string =>
  email.trim().toLowerCase();

export type ParsedEmails = {
  /** Valid, normalised, in the order typed, with duplicates dropped. */
  emails: string[];
  /** What was typed but is not an email, so the dialog can name it. */
  invalid: string[];
};

/**
 * Read the emails out of one text field.
 *
 * The dialog takes "one or more email addresses", and people paste them in
 * every shape a mail client produces: commas, semicolons, newlines, or just
 * spaces. Splitting on all of them costs nothing and saves the owner from
 * retyping a list.
 */
export const parseEmailList = (input: string): ParsedEmails => {
  const parts = input
    .split(/[\s,;]+/)
    .map((part) => normaliseEmail(part))
    .filter(Boolean);

  const emails: string[] = [];
  const invalid: string[] = [];
  for (const part of parts) {
    if (!EMAIL_SHAPE.test(part)) {
      if (!invalid.includes(part)) invalid.push(part);
      continue;
    }
    if (!emails.includes(part)) emails.push(part);
  }
  return { emails, invalid };
};

/**
 * The wrong-account line on the accept page, or null when the account matches.
 *
 * A signed-out visitor has no email yet, so there is nothing to compare and
 * nothing to say: the page shows "Sign in to accept" instead.
 */
export const inviteAccountMismatch = (
  invitedEmail: string | undefined | null,
  signedInEmail: string | undefined | null,
): string | null => {
  const invited = normaliseEmail(invitedEmail ?? "");
  const signedIn = normaliseEmail(signedInEmail ?? "");
  if (!invited || !signedIn) return null;
  if (invited === signedIn) return null;
  return `This invite is for ${invited}. You are signed in as ${signedIn}.`;
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The "Last active" cell.
 *
 * The API stamps this at most once every five minutes, so anything finer than
 * "Just now" would be a precision the data does not have.
 */
export const lastActiveLabel = (
  iso: string | null | undefined,
  now: Date = new Date(),
): string => {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const ago = now.getTime() - then;
  if (ago < 10 * MINUTE) return "Just now";
  if (ago < HOUR) return `${Math.floor(ago / MINUTE)} minutes ago`;
  if (ago < DAY) {
    const hours = Math.floor(ago / HOUR);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }
  const days = Math.floor(ago / DAY);
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

export type Member = {
  id: string;
  email: string;
  name?: string;
  role: string;
  /** Slice 2a name. Slice 1 clients still send `accessRole`. */
  access?: AccessKind;
  accessRole: AccessKind;
  roleLabel: string;
  accessSummary: string;
  tabRules?: TabRule[];
  /** Row filters whose column is missing or ambiguous. The owner banner sums these. */
  brokenRuleCount?: number;
  accepted: boolean;
  lastActiveAt?: string | null;
  editAccessRequested?: boolean;
  token?: string;
  emailSent?: boolean;
};

export const accessOf = (member: {
  access?: string | null;
  accessRole?: string | null;
}): AccessKind | null =>
  normaliseAccess(member.access) ?? normaliseAccess(member.accessRole);

/** Custom shows the short rule. Every other role shows the API line. */
export const memberListSummary = (member: Member): string => {
  if (accessOf(member) !== "CUSTOM") return member.accessSummary;
  return customAccessSummary(member.tabRules ?? []);
};

/** What the member list prints in the name column. */
export const memberDisplayName = (member: Member): string =>
  member.name?.trim() || member.email;

/** The owner cannot be removed or re-roled, so those controls are hidden. */
export const isOwnerRow = (member: Member): boolean =>
  accessOf(member) === "OWNER";

/** Owner first, then the people who joined, then pending invites. */
export const sortMembers = (members: Member[]): Member[] =>
  [...members].sort((a, b) => {
    const rank = (member: Member) =>
      isOwnerRow(member) ? 0 : member.accepted ? 1 : 2;
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return memberDisplayName(a).localeCompare(memberDisplayName(b));
  });

/** The link the invitee opens. Same origin, hash route. */
export const inviteLink = (token: string): string =>
  `${window.location.origin}/#/invite/${token}`;

/** What the owner is told after one invite goes out. */
export const inviteNotice = (
  email: string,
  emailSent: boolean,
  role: InvitableRole,
): string =>
  emailSent
    ? `Invite sent to ${email} as ${roleLabel(role)}.`
    : `Invite link ready for ${email} as ${roleLabel(role)}. Send it to them yourself.`;
